import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { generateRandomId } from '../../shared/others/utils';
import type {
  LegacyPalantirToastCallToAction,
  PalantirToastCallToAction,
  PalantirToastCardEmitInfo,
  PalantirToastComponentType,
} from './toast.type';

export interface PalantirToastUploadProgress {
  progress: number;
  type: 'error' | 'sending' | 'sent';
}

export interface PalantirToastProgressMessages {
  error: string;
  sending: string;
  sent: string;
}

@Injectable({
  providedIn: 'root',
})
export class PalantirToastService {
  public toastEmitter$ = new Subject<PalantirToastCardEmitInfo>();

  public closeEmitter$ = new Subject<string>();

  public clearAllEmitter$ = new Subject<void>();

  public show(options: Omit<PalantirToastCardEmitInfo, 'id'>): string {
    const id = generateRandomId();
    this.toastEmitter$.next({ id, ...options });
    return id;
  }

  public showInfo(
    message: string,
    timeout?: number,
    callToAction?: PalantirToastCallToAction,
  ): string {
    return this.show({
      message,
      theme: 'info',
      timeout,
      callToAction,
    });
  }

  public showSuccess(
    message: string,
    timeout?: number,
    callToAction?: PalantirToastCallToAction,
  ): string;
  /**
   * @deprecated Use `showSuccess` with `PalantirToastCallToAction` instead.
   */
  public showSuccess(
    message: string,
    timeout: number | undefined,
    callToAction: LegacyPalantirToastCallToAction,
  ): string;
  public showSuccess(
    message: string,
    timeout?: number,
    callToAction?: LegacyPalantirToastCallToAction | PalantirToastCallToAction,
  ): string {
    const polyFilledCallToAction: PalantirToastCallToAction | undefined =
      callToAction && typeof callToAction === 'function'
        ? {
            action: async (_, event) => {
              event.preventDefault();
              await callToAction();
            },
            label: 'Ação',
            icon: 'arrow-circle-right-outline',
            closeAfterAction: true,
          }
        : callToAction;

    return this.show({
      message,
      theme: 'success',
      timeout,
      callToAction: polyFilledCallToAction,
    });
  }

  public showWarning(
    message: string,
    timeout?: number,
    callToAction?: PalantirToastCallToAction,
  ): string {
    return this.show({
      message,
      theme: 'warning',
      timeout,
      callToAction,
    });
  }

  public showError(
    message: string,
    timeout?: number,
    callToAction?: PalantirToastCallToAction,
  ): string {
    return this.show({
      message,
      theme: 'error',
      timeout,
      callToAction,
    });
  }

  public showLoading(
    message: string,
    timeout?: number,
    mode?: 'bar' | 'wheel',
    callToAction?: PalantirToastCallToAction,
  ): string {
    return this.show({
      message,
      theme: !mode || mode === 'wheel' ? 'loading' : 'info',
      timeout,
      progress: !mode || mode === 'wheel' ? null : 'indeterminate',
      callToAction,
    });
  }

  public showProgressAlert(
    progress$: Observable<PalantirToastUploadProgress>,
    messages: PalantirToastProgressMessages,
  ) {
    const id = generateRandomId();

    this.toastEmitter$.next({
      id,
      message: messages.sending,
      theme: 'info',
      progress: 0,
      timeout: 5 * 60 * 1_000,
    });

    const subscription = progress$.subscribe((uploadProgress) => {
      if (uploadProgress.type === 'sending') {
        this.toastEmitter$.next({
          id,
          message: messages.sending,
          theme: 'info',
          progress: uploadProgress.progress,
          timeout: 5 * 60 * 1_000,
        });
        return;
      }

      if (uploadProgress.type === 'sent') {
        this.toastEmitter$.next({
          id,
          message: messages.sent,
          theme: 'success',
          progress: uploadProgress.progress,
          timeout: 3_000,
        });

        subscription.unsubscribe();
      }

      if (uploadProgress.type === 'error') {
        this.toastEmitter$.next({
          id,
          message: messages.error,
          theme: 'error',
          progress: null,
          timeout: 3_000,
        });

        subscription.unsubscribe();
      }
    });

    return id;
  }

  /**
   * Atualiza um toast existente, caso o id não exista, um novo toast será criado
   *
   * @param id - Id retornado por qualquer método de exibição de toast
   * @param message - A mensagem a ser exibida
   * @param progress - O progresso da operação (0 a 100) ou null se não houver progresso
   * @param theme - O tema do toast
   */
  public update(
    id: string,
    message: string,
    progress?: number | null,
    theme?: PalantirToastComponentType,
    callToAction?: PalantirToastCallToAction,
  ): void {
    this.toastEmitter$.next({
      id,
      message,
      progress,
      theme: theme ?? 'info',
      callToAction,
    });
  }

  /**
   * Fecha um toast, caso o id não exista, nada acontece
   *
   * @param id - Id retornado por qualquer método de exibição de toast
   */
  public close(id: string): void {
    this.closeEmitter$.next(id);
  }

  /**
   * Fecha todos os toasts
   */
  public clearAll(): void {
    this.clearAllEmitter$.next();
  }
}
